import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lock, MessageCircle, ShieldCheck } from "lucide-react";

import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { toast } from "../components/ui/sonner";
import { useCart } from "../context/CartContext";
import { createDirectOrder } from "../lib/api";
import { formatPrice } from "../lib/filters";
import { WHATSAPP_URL } from "../lib/site";

const REQUIRED = {
  name: "Full name",
  phone: "Phone number",
  address: "Address",
  pincode: "Pincode",
};

const inputCls =
  "w-full rounded-sm border border-beige bg-ivory px-4 py-3 text-sm text-charcoal placeholder:text-taupe focus:outline-none focus:border-charcoal transition-colors";

const Field = ({ label, error, children }) => (
  <label className="block">
    <span className="flex items-baseline justify-between text-xs font-medium text-charcoal/70">
      <span>{label}</span>
      <span className="text-charcoal/40">required</span>
    </span>
    <div className="mt-2">{children}</div>
    {error && <span className="mt-1.5 block text-xs text-[#a33]">{error}</span>}
  </label>
);

export default function Checkout() {
  const { items, subtotal, count } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const autoFallback = location.state?.auto === true;

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const hasItems = items.length > 0;
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your full name";
    if (!/^\d{10}$/.test(form.phone.trim())) next.phone = "Enter a valid 10-digit phone number";
    if (!form.address.trim()) next.address = "Please enter your delivery address";
    if (!/^\d{6}$/.test(form.pincode.trim())) next.pincode = "Enter a valid 6-digit pincode";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const placeOrder = async () => {
    if (items.length === 0 || submitting) return;
    if (!validate()) return;

    const lines = items.map((i) => ({
      id: i.id,
      name: i.name,
      price: i.price,
      image: i.image || null,
      qty: i.qty,
      type: i.type === "addon" ? "addon" : "vanity",
      width: i.width ?? null,
      finish: i.finish ?? null,
      sink: i.sink ?? null,
      mounting: i.mounting ?? null,
      style: i.style ?? null,
      category: i.category ?? null,
    }));

    const customer = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim() || undefined,
      address: form.address.trim(),
      city: form.city.trim() || undefined,
      state: form.state.trim() || undefined,
      pincode: form.pincode.trim(),
    };

    setSubmitting(true);
    try {
      const { order_id } = await createDirectOrder(lines, customer);
      navigate(`/order/confirmed?oid=${order_id}&ost=SUCCESS`);
    } catch (err) {
      setSubmitting(false);
      toast.error("Couldn't place the order", {
        description: "Please try again or reach us on WhatsApp to complete your order.",
      });
    }
  };

  return (
    <div data-testid="checkout-page" className="bg-ivory min-h-screen">
      <Header />

      <main className="pt-28 md:pt-32 pb-28 lg:pb-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-8">
            <div>
              <h1 className="font-serif font-light text-charcoal text-4xl md:text-6xl leading-[1.02] tracking-tight">
                Backup Checkout
              </h1>
              <p className="mt-3 eyebrow text-taupe">
                {count} {count === 1 ? "item" : "items"} · Cash on delivery · Team confirms on WhatsApp
              </p>
            </div>
            <Link
              to="/cart"
              className="inline-flex items-center gap-2 text-sm font-medium text-charcoal link-sweep"
            >
              <ArrowLeft size={15} strokeWidth={1.5} /> Back to cart
            </Link>
          </div>

          {autoFallback && (
            <div
              data-testid="checkout-fallback-notice"
              className="mb-10 border border-beige bg-stone/50 rounded-sm px-5 py-4 text-sm text-charcoal/75 leading-relaxed"
            >
              The fast secure checkout wasn't available, so we've brought you here. Complete your
              details below and our team will confirm your order on WhatsApp before dispatch —{" "}
              <span className="font-medium text-charcoal">no payment needed now</span>.
            </div>
          )}

          {!hasItems ? (
            <div className="text-center py-20">
              <h2 className="font-serif font-light text-charcoal text-3xl md:text-4xl">Your cart is empty</h2>
              <Link
                to="/vanities"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-charcoal text-ivory px-8 py-4 text-sm font-medium btn-fill hover:text-charcoal transition-colors duration-500"
              >
                Shop Vanities <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          ) : (
            <div className="lg:grid lg:grid-cols-[1fr_390px] lg:gap-14">
              {/* Left: form */}
              <section data-testid="checkout-form">
                <div className="border border-beige rounded-sm p-7 md:p-9">
                  <h2 className="font-serif text-2xl text-charcoal">Delivery details</h2>

                  <div className="mt-7 grid sm:grid-cols-2 gap-5">
                    <Field label="Full name" error={errors.name}>
                      <input
                        data-testid="checkout-name"
                        className={inputCls}
                        placeholder="As on your delivery"
                        value={form.name}
                        onChange={set("name")}
                      />
                    </Field>
                    <Field label="Phone number" error={errors.phone}>
                      <input
                        data-testid="checkout-phone"
                        className={inputCls}
                        placeholder="10-digit mobile number"
                        inputMode="numeric"
                        value={form.phone}
                        onChange={set("phone")}
                      />
                    </Field>
                    <div className="sm:col-span-2">
                      <label className="block">
                        <span className="flex items-baseline justify-between text-xs font-medium text-charcoal/70">
                          <span>Email</span>
                          <span className="text-charcoal/40">optional</span>
                        </span>
                        <div className="mt-2">
                          <input
                            data-testid="checkout-email"
                            className={inputCls}
                            placeholder="For order updates"
                            type="email"
                            value={form.email}
                            onChange={set("email")}
                          />
                        </div>
                      </label>
                    </div>
                    <div className="sm:col-span-2">
                      <Field label="Address" error={errors.address}>
                        <textarea
                          data-testid="checkout-address"
                          className={`${inputCls} min-h-24 resize-none`}
                          placeholder="House no, building, street, area"
                          value={form.address}
                          onChange={set("address")}
                        />
                      </Field>
                    </div>
                    <Field label="City" error={errors.city}>
                      <input
                        data-testid="checkout-city"
                        className={inputCls}
                        placeholder="City"
                        value={form.city}
                        onChange={set("city")}
                      />
                    </Field>
                    <Field label="State" error={errors.state}>
                      <input
                        data-testid="checkout-state"
                        className={inputCls}
                        placeholder="State"
                        value={form.state}
                        onChange={set("state")}
                      />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Pincode" error={errors.pincode}>
                        <input
                          data-testid="checkout-pincode"
                          className={inputCls}
                          placeholder="6-digit pincode"
                          inputMode="numeric"
                          value={form.pincode}
                          onChange={set("pincode")}
                        />
                      </Field>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-beige">
                    <h3 className="font-serif text-lg text-charcoal">How payment works</h3>
                    <p className="mt-2 text-sm text-charcoal/70 leading-relaxed">
                      This is a <span className="font-medium text-charcoal">Cash on Delivery</span>{" "}
                      request. No payment is taken now — our team will confirm availability and
                      dispatch timelines on WhatsApp before we deliver. You can pay at the door.
                    </p>
                  </div>
                </div>

                <div className="mt-8 flex items-center gap-4">
                  <span className="grid place-items-center h-10 w-10 rounded-full bg-stone shrink-0">
                    <ShieldCheck size={18} strokeWidth={1.4} className="text-charcoal" />
                  </span>
                  <a
                    href={WHATSAPP_URL("Hi BathLane, I need help completing my checkout.")}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="checkout-whatsapp"
                    className="text-sm text-charcoal/70"
                  >
                    Prefer to order over WhatsApp?{" "}
                    <span className="inline-flex items-center gap-1.5 text-charcoal font-medium link-sweep">
                      <MessageCircle size={14} strokeWidth={1.5} /> Chat with us
                    </span>
                  </a>
                </div>
              </section>

              {/* Right: sticky summary */}
              <aside data-testid="checkout-summary" className="mt-14 lg:mt-0">
                <div className="lg:sticky lg:top-28">
                  <div className="border border-beige rounded-sm p-7 md:p-8 bg-ivory">
                    <h2 className="font-serif text-2xl text-charcoal">Order Summary</h2>

                    <div className="mt-6 space-y-3.5">
                      {items.map((i) => (
                        <div key={i.lid} className="flex items-baseline justify-between gap-4 text-sm">
                          <span className="text-charcoal/70 truncate">
                            {i.name}{" "}
                            <span className="text-taupe">× {i.qty}</span>
                          </span>
                          <span className="text-charcoal font-medium whitespace-nowrap">
                            {formatPrice(i.price * i.qty)}
                          </span>
                        </div>
                      ))}
                      <div className="pt-3 border-t border-beige flex items-baseline justify-between">
                        <span className="text-charcoal/70">Dispatch</span>
                        <span className="text-charcoal/70 text-right">Confirmed on WhatsApp</span>
                      </div>
                      <div className="flex items-baseline justify-between text-xs">
                        <span className="text-taupe">Taxes</span>
                        <span className="text-taupe">Inclusive of all taxes (GST)</span>
                      </div>
                    </div>

                    <div className="mt-6 pt-5 border-t border-beige flex items-center justify-between">
                      <span className="font-serif text-xl text-charcoal">Total</span>
                      <span data-testid="checkout-total" className="font-serif text-2xl text-charcoal">
                        {formatPrice(subtotal)}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-charcoal/55">Payable to our team on delivery (COD).</p>

                    <button
                      onClick={placeOrder}
                      disabled={submitting}
                      data-testid="place-order"
                      className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-charcoal text-ivory py-4 text-sm font-medium btn-fill hover:text-charcoal transition-colors duration-500 disabled:opacity-50"
                    >
                      {submitting ? "Placing order…" : "Place COD Order"} <ArrowRight size={16} strokeWidth={1.5} />
                    </button>

                    <Link
                      to="/cart"
                      className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-beige py-3.5 text-sm font-medium text-charcoal hover:border-charcoal transition-colors duration-300"
                    >
                      Back to Cart
                    </Link>
                  </div>

                  <div className="mt-5 flex items-center gap-2.5 border border-beige rounded-sm px-4 py-3.5">
                    <Lock size={15} strokeWidth={1.4} className="text-charcoal shrink-0" />
                    <span className="text-xs text-charcoal/75">
                      Your details are shared only with our team to confirm and deliver your order.
                    </span>
                  </div>
                </div>
              </aside>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}